import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  getNotificationPreferences,
  getNotifications,
  getWebPushPublicKey,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  patchNotificationPreferences,
  subscribeWebPush,
  trackNotificationInteraction,
} from '../../services/api';
import {
  detectNotificationPortal,
  resolveNotificationInboxPath,
  resolveNotificationLink,
} from '../../utils/notificationPortal';
import {
  syncUnreadCountAfterRead,
  useNotificationUnread,
} from '../../contexts/NotificationUnreadContext';

type NotificationRow = {
  id: string;
  type?: string;
  title?: string;
  message?: string;
  status?: string;
  dateCreation?: string;
  deepLink?: string | null;
  payload?: Record<string, unknown> | null;
};

type NotificationPreferences = {
  categories: {
    commande: boolean;
    paiement: boolean;
    securite: boolean;
    promo: boolean;
    systeme: boolean;
  };
  quietHours: {
    enabled: boolean;
    start?: string | null;
    end?: string | null;
    timezone?: string | null;
  };
};

interface NotificationBellProps {
  buttonClassName?: string;
  iconSize?: number;
  iconTone?: 'primary' | 'muted';
}

function formatShortDate(iso?: string) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function notificationTypeLabel(type?: string) {
  const t = (type || '').toUpperCase();
  if (t.includes('ACADEMIC_RESULTS')) return 'Résultats';
  if (t.includes('DOCUMENT_PENDING')) return 'Dossier';
  if (t.includes('DOCUMENT_VALIDATED')) return 'Dossier validé';
  if (t.includes('DOCUMENT_REJECTED')) return 'Dossier refusé';
  if (t.includes('ADMIN_ACADEMIC')) return 'Admin';
  if (t.includes('ACADEMIC_ALERT')) return 'Alerte';
  if (t.includes('PAY') || t.includes('WALLET') || t.includes('FLEX')) return 'Paiement';
  if (t.includes('WITHDRAWAL')) return 'Retrait';
  if (t.includes('SECUR') || t.includes('AUTH')) return 'Sécurité';
  if (t.includes('REFERRAL')) return 'Parrainage';
  if (t.includes('STAGE')) return 'Stage';
  return 'Info';
}

function notificationIcon(type?: string): string {
  const t = (type || '').toUpperCase();
  if (t.includes('ACADEMIC_RESULTS')) return 'emoji_events';
  if (t.includes('DOCUMENT_PENDING')) return 'folder_shared';
  if (t.includes('DOCUMENT_VALIDATED')) return 'check_circle';
  if (t.includes('DOCUMENT_REJECTED')) return 'cancel';
  if (t.includes('ADMIN_ACADEMIC') || t.includes('ACADEMIC_ALERT')) return 'campaign';
  if (t.includes('PAY') || t.includes('WALLET')) return 'account_balance_wallet';
  if (t.includes('WITHDRAWAL')) return 'payments';
  if (t.includes('SECUR') || t.includes('AUTH')) return 'shield';
  if (t.includes('REFERRAL')) return 'groups';
  if (t.includes('STAGE')) return 'work';
  return 'notifications';
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

const PREF_LABELS: Record<keyof NotificationPreferences['categories'], string> = {
  commande: 'Formations & inscriptions',
  paiement: 'Paiements',
  securite: 'Sécurité',
  promo: 'Promotions & parrainage',
  systeme: 'Annonces système',
};

export default function NotificationBell({
  buttonClassName = 'relative flex h-10 w-10 items-center justify-center rounded-lg bg-white shadow-sm transition-colors hover:bg-primary/5',
  iconSize = 20,
  iconTone = 'primary',
}: NotificationBellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const inboxPath = resolveNotificationInboxPath(location.pathname);
  const portal = detectNotificationPortal(location.pathname);
  const { unreadCount, refreshUnread, setUnreadCount, decrementUnread, clearUnread } =
    useNotificationUnread();
  const [panelOpen, setPanelOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [quietStartDraft, setQuietStartDraft] = useState('');
  const [quietEndDraft, setQuietEndDraft] = useState('');
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const registerWebPush = async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        const publicKey = await getWebPushPublicKey();
        if (!publicKey) return;
        const registration = await navigator.serviceWorker.register('/web-push-sw.js');
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          });
        }
        const keys = subscription.toJSON().keys;
        if (!keys?.auth || !keys?.p256dh) return;
        await subscribeWebPush({
          endpoint: subscription.endpoint,
          keys,
          userAgent: navigator.userAgent || null,
        });
      } catch {
        // web push non bloquant
      }
    };
    void registerWebPush();
  }, []);

  useEffect(() => {
    setPanelOpen(false);
    setPrefsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onDocMouseDown = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setPanelOpen(false);
        setPrefsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPanelOpen(false);
        setPrefsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [panelOpen]);

  const loadPanel = useCallback(async () => {
    setListLoading(true);
    try {
      const result = await getNotifications({
        page,
        limit: 8,
        status: 'UNREAD',
      });
      const unreadItems = (result.items || []) as NotificationRow[];
      setItems(unreadItems);
      setUnreadCount(result.unreadCount || 0);
      setTotalPages(Math.max(1, result?.pagination?.totalPages || 1));
      for (const item of unreadItems.slice(0, 8)) {
        void trackNotificationInteraction(item.id, 'OPEN', 'web');
      }
    } finally {
      setListLoading(false);
    }
  }, [page, setUnreadCount]);

  useEffect(() => {
    if (panelOpen) void loadPanel();
  }, [panelOpen, loadPanel]);

  useEffect(() => {
    if (!panelOpen) return;
    void getNotificationPreferences()
      .then((data) => setPreferences((data || null) as NotificationPreferences | null))
      .catch(() => setPreferences(null));
  }, [panelOpen]);

  const handleMarkRead = async (id: string) => {
    setMarkingId(id);
    decrementUnread();
    setItems((prev) => prev.filter((item) => item.id !== id));
    try {
      const result = await markNotificationAsRead(id);
      await trackNotificationInteraction(id, 'CLICK', 'web');
      await syncUnreadCountAfterRead({ setUnreadCount, refreshUnread }, result?.unreadCount);
      await loadPanel();
    } catch {
      await refreshUnread();
      await loadPanel();
    } finally {
      setMarkingId(null);
    }
  };

  const handleViewNotification = async (item: NotificationRow) => {
    if (!portal) return;
    const actionLink = resolveNotificationLink(item, portal);
    const wasUnread = item.status !== 'READ';
    setMarkingId(item.id);
    if (wasUnread) {
      decrementUnread();
      setItems((prev) => prev.filter((row) => row.id !== item.id));
    }
    try {
      if (wasUnread) {
        const result = await markNotificationAsRead(item.id);
        await trackNotificationInteraction(item.id, 'CLICK', 'web');
        await syncUnreadCountAfterRead({ setUnreadCount, refreshUnread }, result?.unreadCount);
      }
      setPanelOpen(false);
      setPrefsOpen(false);
      if (actionLink) {
        navigate(actionLink);
      }
    } catch {
      if (wasUnread) {
        await refreshUnread();
      }
    } finally {
      setMarkingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    clearUnread();
    setItems([]);
    try {
      const result = await markAllNotificationsAsRead();
      await syncUnreadCountAfterRead({ setUnreadCount, refreshUnread }, result?.unreadCount);
      await loadPanel();
    } catch {
      await refreshUnread();
      await loadPanel();
    } finally {
      setMarkingAll(false);
    }
  };

  const updatePreferenceCategory = async (
    key: keyof NotificationPreferences['categories'],
    value: boolean,
  ) => {
    if (!preferences || savingPrefs) return;
    setSavingPrefs(true);
    try {
      const next = await patchNotificationPreferences({
        categories: { ...preferences.categories, [key]: value },
      });
      setPreferences(next as NotificationPreferences);
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleSaveQuietHours = async () => {
    if (!preferences || savingPrefs) return;
    setSavingPrefs(true);
    try {
      const next = await patchNotificationPreferences({
        quietHours: {
          ...preferences.quietHours,
          start: quietStartDraft.trim() || null,
          end: quietEndDraft.trim() || null,
        },
      });
      setPreferences(next as NotificationPreferences);
      setQuietStartDraft('');
      setQuietEndDraft('');
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        aria-expanded={panelOpen}
        aria-label="Notifications"
        title="Notifications"
        className={buttonClassName}
        onClick={() => setPanelOpen((open) => !open)}
      >
        <LucideSymbolIcon
          name={unreadCount > 0 ? 'notifications_active' : 'notifications'}
          tone={iconTone}
          size={iconSize}
        />
        {unreadCount > 0 ? (
          <span className="absolute right-0.5 top-0.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white ring-2 ring-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {panelOpen ? (
        <div className="notification-popover absolute right-0 top-full z-50 mt-2">
          <header className="notification-popover__header">
            <div className="notification-popover__header-main">
              <span className="notification-popover__header-icon" aria-hidden>
                <LucideSymbolIcon name="notifications_active" tone="inherit" size={18} />
              </span>
              <div className="min-w-0">
                <p className="notification-popover__title">Notifications non lues</p>
                <p className="notification-popover__subtitle">
                  {unreadCount > 0
                    ? `${unreadCount} message${unreadCount > 1 ? 's' : ''} en attente`
                    : 'Vous êtes à jour'}
                </p>
              </div>
            </div>
            <div className="notification-popover__header-actions">
              <button
                type="button"
                className="notification-popover__icon-btn"
                title="Tout marquer comme lu"
                aria-label="Tout marquer comme lu"
                disabled={unreadCount <= 0 || markingAll}
                onClick={() => void handleMarkAllRead()}
              >
                <LucideSymbolIcon name="checklist" tone="inherit" size={16} />
              </button>
              <button
                type="button"
                className="notification-popover__icon-btn"
                title="Préférences"
                aria-label="Préférences de notification"
                aria-expanded={prefsOpen}
                onClick={() => setPrefsOpen((open) => !open)}
              >
                <LucideSymbolIcon name="settings" tone="inherit" size={16} />
              </button>
              <button
                type="button"
                className="notification-popover__icon-btn"
                title="Fermer"
                aria-label="Fermer"
                onClick={() => {
                  setPanelOpen(false);
                  setPrefsOpen(false);
                }}
              >
                <LucideSymbolIcon name="close" tone="inherit" size={16} />
              </button>
            </div>
          </header>

          <div className="notification-popover__list">
            {listLoading ? (
              <div className="notification-popover__loading">
                <LucideSymbolIcon name="progress_activity" tone="muted" size={18} className="animate-spin" />
                Chargement…
              </div>
            ) : items.length === 0 ? (
              <div className="notification-popover__empty">
                <span className="notification-popover__empty-icon" aria-hidden>
                  <LucideSymbolIcon name="notifications" tone="inherit" size={22} />
                </span>
                <p className="notification-popover__empty-title">Aucune notification non lue</p>
                <p className="notification-popover__empty-text">
                  Vous êtes à jour. Consultez l’historique complet dans Mes notifications.
                </p>
              </div>
            ) : (
              <ul>
                {items.map((item) => {
                  const actionLink = portal ? resolveNotificationLink(item, portal) : null;
                  return (
                    <li key={item.id} className="notification-popover__item notification-popover__item--unread">
                      <span className="notification-popover__item-icon" aria-hidden>
                        <LucideSymbolIcon name={notificationIcon(item.type)} tone="inherit" size={16} />
                      </span>
                      <div className="notification-popover__item-body">
                        <div className="notification-popover__item-head">
                          <p className="notification-popover__item-title">{item.title || 'Notification'}</p>
                          <span className="notification-popover__item-dot" aria-hidden />
                        </div>
                        <p className="notification-popover__item-message">{item.message || ''}</p>
                        <div className="notification-popover__item-meta">
                          <span className="notification-popover__item-type">
                            {notificationTypeLabel(item.type)}
                          </span>
                          <span className="notification-popover__item-date">
                            {formatShortDate(item.dateCreation)}
                          </span>
                          {actionLink ? (
                            <button
                              type="button"
                              disabled={markingId === item.id}
                              className="notification-popover__mark-btn"
                              onClick={() => void handleViewNotification(item)}
                            >
                              {markingId === item.id ? '…' : 'Voir'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={markingId === item.id}
                              className="notification-popover__mark-btn"
                              onClick={() => void handleMarkRead(item.id)}
                            >
                              {markingId === item.id ? '…' : 'Marquer lu'}
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <footer className="notification-popover__footer">
            {totalPages > 1 ? (
              <div className="notification-popover__pagination">
                <button
                  type="button"
                  disabled={page <= 1}
                  className="notification-popover__page-btn"
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                >
                  Précédent
                </button>
                <span className="notification-popover__page-indicator">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  className="notification-popover__page-btn"
                  onClick={() => setPage((value) => value + 1)}
                >
                  Suivant
                </button>
              </div>
            ) : null}
            {inboxPath ? (
              <Link
                to={inboxPath}
                className="notification-popover__inbox-link"
                onClick={() => {
                  setPanelOpen(false);
                  setPrefsOpen(false);
                }}
              >
                {portal === 'admin' ? 'Mes notifications' : 'Voir toutes les notifications'}
                <LucideSymbolIcon name="arrow_forward" tone="inherit" size={14} />
              </Link>
            ) : null}
          </footer>

          {prefsOpen ? (
            <section className="notification-popover__prefs" aria-label="Préférences de notification">
              <div className="notification-popover__prefs-body">
                {preferences ? (
                  <>
                    {(Object.keys(PREF_LABELS) as Array<keyof typeof PREF_LABELS>).map((key) => (
                      <label key={key} className="notification-popover__pref-row">
                        <span>{PREF_LABELS[key]}</span>
                        <input
                          type="checkbox"
                          checked={preferences.categories[key]}
                          disabled={savingPrefs}
                          onChange={(event) => void updatePreferenceCategory(key, event.target.checked)}
                        />
                      </label>
                    ))}
                    <label className="notification-popover__pref-row">
                      <span>Heures silencieuses</span>
                      <input
                        type="checkbox"
                        checked={preferences.quietHours.enabled}
                        disabled={savingPrefs}
                        onChange={(event) =>
                          void patchNotificationPreferences({
                            quietHours: {
                              ...preferences.quietHours,
                              enabled: event.target.checked,
                            },
                          }).then((next) => setPreferences(next as NotificationPreferences))
                        }
                      />
                    </label>
                    {preferences.quietHours.enabled ? (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={quietStartDraft || preferences.quietHours.start || ''}
                          onChange={(event) => setQuietStartDraft(event.target.value)}
                          placeholder="Début HH:mm"
                          className="rounded-lg border border-outline-variant/25 bg-slate-50 px-2 py-1.5 text-xs"
                        />
                        <input
                          type="text"
                          value={quietEndDraft || preferences.quietHours.end || ''}
                          onChange={(event) => setQuietEndDraft(event.target.value)}
                          placeholder="Fin HH:mm"
                          className="rounded-lg border border-outline-variant/25 bg-slate-50 px-2 py-1.5 text-xs"
                        />
                      </div>
                    ) : null}
                    {preferences.quietHours.enabled ? (
                      <button
                        type="button"
                        disabled={savingPrefs}
                        className="notification-popover__page-btn mt-2 w-full"
                        onClick={() => void handleSaveQuietHours()}
                      >
                        Enregistrer les horaires
                      </button>
                    ) : null}
                  </>
                ) : (
                  <p className="text-xs text-slate-400">Préférences indisponibles.</p>
                )}
              </div>
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
